import { ApolloClient } from '@apollo/client';
import { CONTRACT_LIST_ITEM_QUERY } from './contracts.gql';
import { ContractListItem } from './types';

interface ContractListItemResponse {
	contract?: ContractNode | null;
}

interface ContractNode {
	contractId: string;
	beginDate: string;
	endDate: string;
	amount?: number | null;
	isAutoRenewed: boolean;
	simultaneousVehicleLimit?: number | null;
	isActive: boolean;
	includeAllCustomerVehicles: boolean;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
		customerCategory?: {
			description?: string | null;
			simultaneousVehicleLimit?: number | null;
		} | null;
		vehicles?: Array<{
			vehicleId: string;
			plateNumber?: string | null;
			vehicleType?: {
				name?: string | null;
			} | null;
			make?: string | null;
			model?: string | null;
			color?: string | null;
		}> | null;
	} | null;
	vehicles?: Array<{
		contractId: string;
		vehicleId: string;
		vehicle?: {
			vehicleId: string;
			plateNumber?: string | null;
			make?: string | null;
			model?: string | null;
			color?: string | null;
		} | null;
	}> | null;
	vipVehicles?: string[] | null;
	cards?: Array<{
		contractCardId?: string | null;
		facilityId: string;
		cardType: 'RFID' | 'BARCODE' | 'MAGNETIC';
		cardKey?: string | null;
		cardStatusId: string;
		depositTransactionId?: string | null;
		returnTransactionId?: string | null;
	}> | null;
	parkingSessions?: Array<{
		parkingSessionId: string;
		entryDateTime: string;
		exitDateTime?: string | null;
		status: string;
	}> | null;
}

export interface SelectContractAfterSaveOptions {
	contractId: string;
	client: ApolloClient<any>;
	currentContracts: ContractListItem[];
	onDataUpdate: (updatedContracts: ContractListItem[]) => void;
	onContractSelect: (contract: ContractListItem) => void;
	mapNodeToListItem: (node: ContractNode) => ContractListItem;
}

/**
 * Fetches the latest contract data from the server and updates the list,
 * then selects and highlights the contract in the grid and details panel.
 *
 * @param options Configuration options for selecting the contract
 * @returns Promise that resolves when the operation is complete
 */
export async function selectContractAfterSave(
	options: SelectContractAfterSaveOptions
): Promise<void> {
	const { contractId, client, currentContracts, onDataUpdate, onContractSelect, mapNodeToListItem } = options;

	try {
		// Step 1: Fetch the latest contract data from the server
		const { data } = await client.query<ContractListItemResponse>({
			query: CONTRACT_LIST_ITEM_QUERY,
			variables: { contractId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedContractNode = data?.contract;

		if (!fetchedContractNode) {
			console.warn(`Contract with ID ${contractId} not found after save`);
			return;
		}

		// Map the fetched node to ContractListItem format
		const fetchedContract = mapNodeToListItem(fetchedContractNode);

		// Step 2: Check if contract exists in current list
		const existingContractIndex = currentContracts.findIndex(
			c => c.contractId === contractId
		);

		let updatedContracts: ContractListItem[];

		if (existingContractIndex >= 0) {
			// Contract exists - replace with updated data
			updatedContracts = [...currentContracts];
			updatedContracts[existingContractIndex] = fetchedContract;
		} else {
			// New contract - append to the list
			updatedContracts = [...currentContracts, fetchedContract];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedContracts);

		// Step 4: Select the contract (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onContractSelect(fetchedContract);
		}, 50);
	} catch (error) {
		console.error('Error selecting contract after save:', error);
		throw error;
	}
}
